import { useEffect } from 'react';
import { seoDefaults } from '../data/content';

const CANONICAL_BASE = (process.env.REACT_APP_CANONICAL_BASE || 'https://msgtopdfconverter.com').replace(/\/$/, '');

/**
 * Manages document head tags imperatively (no react-helmet). Renders nothing.
 *
 * Mount this once near the root for the current page. On mount and whenever any
 * prop changes, it updates: <title>, meta description, Open Graph + Twitter
 * tags, canonical <link>, and a JSON-LD SoftwareApplication structured-data
 * block. The canonical base URL comes from REACT_APP_CANONICAL_BASE (falls back
 * to https://msgtopdfconverter.com). Defaults for title/description/image come
 * from src/data/content.js — edit there for site-wide copy changes.
 *
 * @param {string} [props.title]        <title> + og:title + twitter:title.
 * @param {string} [props.description]  meta description + og:description + …
 * @param {string} [props.path='/']     Appended to canonical base for og:url
 *                                      and the canonical href. Must start with '/'.
 * @param {string} [props.image]        og:image + twitter:image + JSON-LD image.
 *                                      Defaults to `${CANONICAL_BASE}${seoDefaults.ogImagePath}`.
 * @returns {null}                      Side-effect-only component.
 */
const SEO = ({
    title = seoDefaults.title,
    description = seoDefaults.description,
    path = '/',
    image = `${CANONICAL_BASE}${seoDefaults.ogImagePath}`
}) => {
    useEffect(() => {

        // Update Title
        document.title = title;

        // Helper to update or create meta tags
        const updateMeta = (name, content, attribute = 'name') => {
            let element = document.querySelector(`meta[${attribute}="${name}"]`);
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attribute, name);
                document.head.appendChild(element);
            }
            element.setAttribute('content', content);
        };

        // Update Meta Description
        updateMeta('description', description);

        // Update Open Graph tags
        updateMeta('og:title', title, 'property');
        updateMeta('og:description', description, 'property');
        updateMeta('og:url', `${CANONICAL_BASE}${path}`, 'property');
        updateMeta('og:image', image, 'property');
        updateMeta('og:type', 'website', 'property');

        // Update Twitter tags
        updateMeta('twitter:card', 'summary_large_image', 'name');
        updateMeta('twitter:title', title, 'name');
        updateMeta('twitter:description', description, 'name');
        updateMeta('twitter:image', image, 'name');

        // Update Canonical Tag
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
        }
        link.setAttribute('href', `${CANONICAL_BASE}${path}`);

        // Add JSON-LD Structured Data
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": seoDefaults.brandName,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Any",
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
            },
            "description": description,
            "url": CANONICAL_BASE,
            "image": image,
            "featureList": "Convert MSG to PDF, Convert EML to PDF, Secure file handling",
            "softwareRequirements": "Modern Web Browser"
        };

        let script = document.querySelector('script[type="application/ld+json"]');
        if (!script) {
            script = document.createElement('script');
            script.setAttribute('type', 'application/ld+json');
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(structuredData);

    }, [title, description, path, image]);

    return null;
};

export default SEO;
